import React, { useState, useEffect } from "react";
import {
    Box,
    Avatar,
    Typography,
    Button,
    LinearProgress,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useParams, useNavigate } from "react-router-dom";
import * as S from "./styles";
import crownImage from "../../_assets/images/wizard/crown.png";
import { apiGetRanking } from "../../services/apiGetRanking";
import { apiGetRankingPeriodos } from "../../services/apiGetRankingPeriodos";
import { getCurrentPeriod } from "../../utils/getCurrentPeriod";
import { formatDateString } from "../../utils/formatDateString";
import { useMobileDetect } from "../../hooks";
import { useAuthStore } from "../../store/useAuthStore";
import { WIZARD_COMMUNITY_ID } from "../../constants/wizard";

// The list always shows at least up to this position; when the period awards
// more than that, it extends to include all winners.
const MIN_LIST_POSITION = 15;

// Some periods don't send the winners count; in that case, we default to 3.
const DEFAULT_WINNERS_COUNT = 3;

export const Ranking = () => {
    const navigator = useNavigate();
    const [rankingData, setRankingData] = useState([]);
    const [rankingPeriodos, setRankingPeriodos] = useState([]);
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMobile = useMobileDetect();
    const { creator } = useAuthStore();

    useEffect(() => {
        const fetchRanking = async () => {
            try {
                setIsLoading(true);

                const periodsResponse = await apiGetRankingPeriodos({ comunidade: WIZARD_COMMUNITY_ID });
                setRankingPeriodos(periodsResponse);

                const period = getCurrentPeriod(periodsResponse);
                setCurrentPeriod(period);

                if (period && !selectedPeriod) {
                    setSelectedPeriod(period.id);
                }

                const periodToUse = selectedPeriod ? periodsResponse.find((p) => p.id === selectedPeriod) : period;

                if (periodToUse) {
                    const rankingResponse = await apiGetRanking({ comunidade: WIZARD_COMMUNITY_ID, periodo: periodToUse.id });

                    const updatedRankingData = rankingResponse.map((user) => ({
                        ...user,
                        current_user: creator && Number(user.creator) === Number(creator.id),
                    }));

                    setRankingData(updatedRankingData);
                } else {
                    setRankingData([]);
                }
            } catch (err) {
                setError("Não foi possível carregar o ranking");
            } finally {
                setIsLoading(false);
            }
        };

        fetchRanking();
    }, [selectedPeriod, creator]);

    if (!WIZARD_COMMUNITY_ID) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="error">ID da comunidade não encontrado</Typography>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const calculateProgress = (startDate, endDate) => {
        const now = new Date();
        const total = new Date(endDate) - new Date(startDate);
        const elapsed = now - new Date(startDate);
        return Math.min(100, Math.max(0, (elapsed / total) * 100));
    };

    const activePeriod = selectedPeriod ? rankingPeriodos.find((p) => p.id === selectedPeriod) : currentPeriod;

    const periodProgress = activePeriod ? calculateProgress(activePeriod.data_inicio, activePeriod.data_fim) : 0;

    const handlePeriodChange = async (event) => {
        const newPeriodId = event.target.value;
        setSelectedPeriod(newPeriodId);

        try {
            setIsLoading(true);
            const rankingResponse = await apiGetRanking({ comunidade: WIZARD_COMMUNITY_ID, periodo: newPeriodId });

            const updatedRankingData = rankingResponse.map((user) => ({
                ...user,
                current_user: creator && Number(user.creator) === Number(creator.id),
            }));

            setRankingData(updatedRankingData);
        } catch (err) {
            setError("Não foi possível carregar o ranking");
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewUserDetail = (creatorId) => {
        navigator(`/ranking/creator?creator=${creatorId}&comunidade=${WIZARD_COMMUNITY_ID}&periodo=${selectedPeriod}`);
    };

    // Full-row highlight by position, only for winners: 1st gold, 2nd silver,
    // 3rd bronze, 4th brand (dark blue), and from 5th onward brandLight. Outside the rewards, neutral.
    const getTierStyles = (position, isWinner) => {
        const tiers = {
            gold: { accent: "#F0A400", rowBg: "linear-gradient(90deg, #FFE486 0%, #FFF4C9 100%)", badgeText: "#fff" },
            silver: { accent: "#AEB7C0", rowBg: "linear-gradient(90deg, #E6EAEE 0%, #F6F8FA 100%)", badgeText: "#2f3a42" },
            bronze: { accent: "#C07B3B", rowBg: "linear-gradient(90deg, #F0D6BC 0%, #FBF1E7 100%)", badgeText: "#fff" },
            brand: { accent: "#002F5D", rowBg: "linear-gradient(90deg, #C7D9E6 0%, #EAF1F6 100%)", badgeText: "#fff" },
            brandLight: { accent: "#00A3FF", rowBg: "linear-gradient(90deg, #CFEBFF 0%, #EAF6FF 100%)", badgeText: "#fff" },
        };

        let key = null;
        if (isWinner) {
            if (position === 1) key = "gold";
            else if (position === 2) key = "silver";
            else if (position === 3) key = "bronze";
            else if (position === 4) key = "brand";
            else key = "brandLight";
        }

        if (!key) {
            return {
                row: { background: "#fff", borderLeft: "5px solid transparent" },
                badge: { bgcolor: "#F0F0F3", color: "#9a9aa5" },
                avatarBorder: "2px solid transparent",
            };
        }

        const t = tiers[key];
        return {
            row: { background: t.rowBg, borderLeft: `5px solid ${t.accent}` },
            badge: { bgcolor: t.accent, color: t.badgeText },
            avatarBorder: `2px solid ${t.accent}`,
        };
    };

    const renderUserItem = (user, isWinner = false, highlightCurrent = true) => {
        const isChampion = isWinner && user.position === 1;
        const isCurrent = !!user.current_user;
        const highlight = isCurrent && highlightCurrent;
        const tier = getTierStyles(user.position, isWinner);

        return (
            <Box
                key={user.creator}
                onClick={() => handleViewUserDetail(user.creator)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: isWinner ? 1.5 : 1.25,
                    position: "relative",
                    cursor: "pointer",
                    borderBottom: "1px solid #f2f2f4",
                    "&:last-child": { borderBottom: "none" },
                    ...tier.row,
                    // Highlight for the logged-in user: a rounded "card" with margin,
                    // so the border follows the corners and is never clipped by the
                    // container's rounded overflow (avoids squared-off corners).
                    ...(highlight
                        ? {
                              m: 1,
                              borderRadius: 3,
                              border: "2px solid var(--primary-color)",
                              borderBottom: "2px solid var(--primary-color)",
                              "&:last-child": { borderBottom: "2px solid var(--primary-color)" },
                          }
                        : {}),
                }}
            >
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        flexShrink: 0,
                        ...tier.badge,
                    }}
                >
                    {user.position}
                </Box>

                <Box sx={{ position: "relative", flexShrink: 0, display: "flex" }}>
                    {isChampion && (
                        <Box
                            component="img"
                            src={crownImage}
                            alt="Campeão"
                            sx={{
                                position: "absolute",
                                top: -13,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 24,
                                height: "auto",
                                zIndex: 2,
                                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.25))",
                            }}
                        />
                    )}
                    <Avatar
                        src={
                            user.avatar_url ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                user.creator_name || user.position
                            )}&background=FF4B55&color=FFFFFF`
                        }
                        sx={{
                            width: 42,
                            height: 42,
                            border: tier.avatarBorder,
                        }}
                    />
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: isWinner ? 700 : 600,
                            color: "#1a1a2e",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {user.creator_name}
                        {isCurrent ? " (você)" : ""}
                    </Typography>
                </Box>

                <Box sx={{ textAlign: "right", flexShrink: 0, ml: 1 }}>
                    <Typography component="span" sx={{ fontWeight: 800, color: "#00A3FF", fontSize: "0.95rem" }}>
                        {user.points}
                    </Typography>
                    <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.7rem", ml: 0.5 }}>
                        pts
                    </Typography>
                </Box>
            </Box>
        );
    };

    // How many winners the period awards. The API sends "winners_count"; when
    // the period doesn't include the field, we default to DEFAULT_WINNERS_COUNT.
    const parsedWinnersCount = Number(activePeriod?.winners_count ?? activePeriod?.winnersCount);
    const winnersCount =
        Number.isFinite(parsedWinnersCount) && parsedWinnersCount > 0
            ? parsedWinnersCount
            : DEFAULT_WINNERS_COUNT;

    // Shows at least the top 10; extends to include all winners.
    const listEnd = Math.max(MIN_LIST_POSITION, winnersCount);
    const displayList = rankingData.filter((user) => user.position <= listEnd);

    const isCurrentUserInList = displayList.some((user) => user.current_user);

    // The ranking only includes users who scored. If the logged-in creator still
    // has 0 points, they won't be in the response; we build a synthetic row to
    // show their position (right after everyone who scored) with 0 pts.
    const currentUserInData = rankingData.find((user) => user.current_user);
    const currentUser =
        currentUserInData ||
        (creator
            ? {
                  creator: creator.id,
                  creator_name: creator.nome,
                  avatar_url: "",
                  points: 0,
                  position: rankingData.length + 1,
                  current_user: true,
              }
            : null);

    const handleGoBack = () => navigator(-1);

    return (
        <S.Container>
            {!isMobile && (
                <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    width="100%"
                    sx={{ px: 2, pt: 3 }}
                ></Box>
            )}
            <Box
                sx={{
                    px: 2,
                    pt: 3,
                    pb: { xs: 10, sm: 12 },
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <S.Title>Ranking</S.Title>
                    {rankingPeriodos.length > 0 && (
                        <FormControl sx={{ minWidth: 200 }} size="small">
                            <InputLabel id="period-select-label">Período</InputLabel>
                            <Select
                                labelId="period-select-label"
                                id="period-select"
                                value={selectedPeriod || ""}
                                label="Período"
                                onChange={handlePeriodChange}
                            >
                                {rankingPeriodos
                                    .sort((a, b) => new Date(b.data_inicio) - new Date(a.data_inicio))
                                    .map((period) => (
                                        <MenuItem key={period.id} value={period.id}>
                                            {formatDateString(period.data_inicio)} - {formatDateString(period.data_fim)}
                                            {period.id === currentPeriod?.id ? " (atual)" : ""}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                {activePeriod && (
                    <S.ProgressSection>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            PERÍODO DE PONTUAÇÃO
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {formatDateString(activePeriod.data_inicio)} até {formatDateString(activePeriod.data_fim)}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={periodProgress}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: "#eee",
                                "& .MuiLinearProgress-bar": {
                                    bgcolor: "#FF4B55",
                                },
                            }}
                        />
                        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                            {Math.round(periodProgress)}% do período concluído
                        </Typography>
                    </S.ProgressSection>
                )}

                {winnersCount > 0 && (
                    <S.RewardBanner>
                        <EmojiEventsIcon sx={{ fontSize: 20, color: "#FFB300" }} />
                        {winnersCount === 1
                            ? "A primeira posição é premiada!"
                            : `As ${winnersCount} primeiras posições são premiadas nesse período!`}
                    </S.RewardBanner>
                )}

                {displayList.length > 0 && (
                    <S.RankingListContainer>
                        {displayList.map((user, index) => {
                            const isWinner = user.position <= winnersCount;
                            const prevWasWinner =
                                index > 0 && displayList[index - 1].position <= winnersCount;
                            const showCutoff = prevWasWinner && !isWinner;

                            return (
                                <React.Fragment key={user.creator}>
                                    {showCutoff && <S.RankingDivider>Demais posições</S.RankingDivider>}
                                    {renderUserItem(user, isWinner)}
                                </React.Fragment>
                            );
                        })}
                    </S.RankingListContainer>
                )}

                {!isCurrentUserInList && currentUser && (
                    <Box sx={{ mt: 3 }}>
                        <Typography sx={{ fontWeight: 600, color: "text.secondary", mb: 1, fontSize: "0.85rem" }}>
                            Sua posição
                        </Typography>
                        <S.RankingListContainer sx={{ border: "2px solid var(--primary-color)" }}>
                            {renderUserItem(
                                currentUser,
                                currentUser.points > 0 && currentUser.position <= winnersCount,
                                false
                            )}
                        </S.RankingListContainer>
                    </Box>
                )}

                <S.CallToAction>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        CONTINUE ACUMULANDO PONTOS!
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                        Participe ativamente na plataforma para subir no ranking e desbloquear recompensas exclusivas.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigator("/gamification")}
                        sx={{
                            bgcolor: "#00A3FF",
                            borderRadius: 2,
                            textTransform: "none",
                            py: 1.5,
                            px: 4,
                            "&:hover": {
                                bgcolor: "#0091EA",
                            },
                        }}
                    >
                        Começar a pontuar
                    </Button>
                </S.CallToAction>
            </Box>
        </S.Container>
    );
};
